import TaleButton from "@components/TaleButton";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { useQuery } from "@tanstack/react-query";
import { lazily } from "react-lazily";
import { companyService} from "../../../api/companies";
import AddCompanyPopup from "./AddCompanyPupup";
import {TaleModal} from "../../../components/TaleModal";
import { useTranslation } from "@/hooks/useTranslation";

const { InternalServerError } = lazily(() =>
    import("@components/Errors/InternalServerError")
);

const Modal = ({ title, onClose, children }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
            <div className="p-6">{children}</div>
        </div>
    </div>
);

// ── Company row ─────────────────────────────────────────────────────────────
const CompanyRow = ({ company, onEdit, onDelete }) => {
    const { t } = useTranslation();
    const initials = company.companyName
        ?.split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    const logo = company.assets?.find((a) => a.description === "COMPANY_LOGO");

    return (
        <tr className="border-b hover:bg-gray-50 transition-colors">
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    {logo ? (
                        <img src={logo.url} alt={company.companyName} className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold">
                            {initials}
                        </div>
                    )}
                    <span className="font-medium text-gray-900">{company.companyName}</span>
                </div>
            </td>
            <td className="px-4 py-3 text-gray-600">{company.industry || <span className="text-gray-300">—</span>}</td>
            <td className="px-4 py-3 text-gray-600">
                {company.website ? (
                    <a href={company.website} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline truncate max-w-[180px] block">
                        {company.website}
                    </a>
                ) : (
                    <span className="text-gray-300">—</span>
                )}
            </td>
            <td className="px-4 py-3 text-gray-600">{company.address || <span className="text-gray-300">—</span>}</td>
            <td className="px-4 py-3 text-sm text-gray-500">
                {company.createdAt
                    ? new Date(company.createdAt).toLocaleDateString("fr-FR")
                    : <span className="text-gray-300">—</span>
                }
            </td>
            <td className="px-4 py-3">
                <div className="flex gap-2">
                    <TaleButton
                        size="sm"
                        variant="outlined"
                        onClick={() => onEdit(company)}
                    >
                        <i className="fas fa-edit mr-1 rtl:mr-0 rtl:ml-1"></i>
                        {t("common.edit")}
                    </TaleButton>
                    <TaleButton
                        role="business"
                        size="sm"
                        variant="outlined"
                        onClick={() => onDelete(company.id)}
                    >
                        <i className="fas fa-trash mr-1 rtl:mr-0 rtl:ml-1"></i>
                        {t("common.delete")}
                    </TaleButton>
                </div>
            </td>
        </tr>
    );
};

// ── Main page ────────────────────────────────────────────────────────────────
const Companies = () => {
    const { t } = useTranslation();
    const PAGE_SIZE = 8;
    const [currentPage, setCurrentPage] = useState(0);
    const [searchFilter, setSearchFilter] = useState("");

    const [companyToDelete, setCompanyToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [companies, setCompanies] = useState([]);

    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const { isPending: loading, data, error, refetch } = useQuery({
        queryKey: ["companies", currentPage],
        queryFn: () => companyService.getCompaniesPaginated(currentPage, PAGE_SIZE),
        retry: false,
    });

    useEffect(() => {
        if (loading || error || !data) return;
        setCompanies(data.result ?? []);
        setTotalPages(data.totalPages ?? 1);
        setTotalItems(data.totalItems ?? 0);
    }, [data, loading, error]);

    useEffect(() => {
        if (loading || error || !data) return;
        setCompanies(Array.isArray(data) ? data : data.result ?? []);
    }, [data, loading, error]);

    const handleSearch = useCallback((value) => {
        setSearchFilter(value);
        setCurrentPage(0);
    }, []);

    const filteredCompanies = useMemo(() => {
        if (!searchFilter) return companies;
        const q = searchFilter.toLowerCase();
        return companies.filter(
            (c) =>
                c.companyName?.toLowerCase().includes(q) ||
                c.industry?.toLowerCase().includes(q) ||
                c.address?.toLowerCase().includes(q)
        );
    }, [companies, searchFilter]);

    // ── Delete ──
    const confirmDelete = useCallback(async () => {
        if (!companyToDelete) return;
        setIsDeleting(true);
        try {
            await companyService.deleteCompany(companyToDelete);
            setCompanies((prev) => prev.filter((c) => c.id !== companyToDelete));
        } catch (err) {
            console.error("Failed to delete company:", err);
        } finally {
            setIsDeleting(false);
            setCompanyToDelete(null);
        }
    }, [companyToDelete]);

    // ── Edit success ──
    const handleEditSuccess = useCallback((updated) => {
        setCompanies((prev) =>
            prev.map((c) => (c.id === updated.id ? updated : c))
        );
        setEditingCompany(null);
    }, []);

    // ── Create success ──
    const handleCreateSuccess = useCallback((newCompany) => {
        setShowCreateModal(false);
        // Optimistically add to list instead of refetching
        if (newCompany) {
            setCompanies((prev) => [newCompany, ...prev]);
        } else {
            refetch();
        }
    }, [refetch]);

    const loadingItem = useMemo(
        () => (
            <div className="size-full z-20 absolute flex items-center justify-center grow">
                <TaleLoadingFallback />
            </div>
        ),
        []
    );

    return (
        <>
            {/* Delete confirmation modal */}
            {companyToDelete && (
                <Modal title={t("manageCompany.confirmDeleteTitle")} onClose={() => setCompanyToDelete(null)}>
                    <p className="text-gray-600 mb-6">
                        {t("manageCompany.confirmDeleteMessage")}
                    </p>
                    <div className="flex justify-end gap-3">
                        <TaleButton variant="outlined" onClick={() => setCompanyToDelete(null)} disabled={isDeleting}>
                            {t("common.cancel")}
                        </TaleButton>
                        <TaleButton onClick={confirmDelete} loading={isDeleting} className="bg-red-600 hover:bg-red-700 border-red-600">
                            {t("common.delete")}
                        </TaleButton>
                    </div>
                </Modal>
            )}

            {/* Edit modal */}
            {editingCompany && (
                <TaleModal className="flex items-end justify-end p-4" onClose={() => setEditingCompany(null)}>
                    <AddCompanyPopup
                        existingCompany={editingCompany}
                        onClose={() => setEditingCompany(null)}
                        onValidate={(updated) => handleEditSuccess(updated)}
                    />
                </TaleModal>
            )}

            {/* Create modal */}
            {showCreateModal && (
                <TaleModal className="flex items-end justify-end p-4" onClose={() => setShowCreateModal(false)}>
                    <AddCompanyPopup
                        onClose={() => setShowCreateModal(false)}
                        onValidate={handleCreateSuccess}
                    />
                </TaleModal>
            )}

            <TaleDashbaordMainCard className="flex flex-col gap-6">
                {/* Header */}
                <div className="w-full flex justify-between items-center">
                    <div className="flex flex-col">
                        <h2 className="!text-2xl font-bold !m-0">{t("manageCompany.title")}</h2>
                        <p className="text-gray-600">{t("manageCompany.subtitle")}</p>
                    </div>
                    <div className="flex gap-3">
                        <TaleButton role="normal" className="!px-4" onClick={() => setShowCreateModal(true)}>
                            + {t("manageCompany.newCompanyButton")}
                        </TaleButton>
                        <TaleButton role="normal" variant="outlined" onClick={() => refetch()}>
                            <i className="fas fa-sync-alt mr-2 rtl:mr-0 rtl:ml-2"></i>{t("manageCompany.refresh")}
                        </TaleButton>
                    </div>
                </div>

                {/* Search
                <div className="flex items-center gap-4">
                    <div className="relative w-full md:w-72">
                        <input
                            type="text"
                            placeholder="Rechercher par nom, secteur, adresse..."
                            className="px-4 py-2 border rounded-lg w-full"
                            value={searchFilter}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                        {searchFilter && (
                            <button
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                onClick={() => handleSearch("")}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        )}
                    </div>
                </div>*/}

                {/* Stats
                {!loading && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="bg-blue-50 p-4 rounded-xl">
                            <div className="text-sm text-blue-600">Total entreprises</div>
                            <div className="text-2xl font-bold">{totalItems}</div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-xl">
                            <div className="text-sm text-green-600">Avec site web</div>
                            <div className="text-2xl font-bold">
                                {companies.filter((c) => c.website).length}
                            </div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-xl">
                            <div className="text-sm text-purple-600">Résultats filtrés</div>
                            <div className="text-2xl font-bold">{filteredCompanies.length}</div>
                        </div>
                    </div>
                )} */}

                {/* Table */}
                <div className={`flex flex-col relative grow size-full ${!filteredCompanies.length && !loading ? "items-center justify-center gap-4" : "gap-4"}`}>
                    {loading && loadingItem}
                    {error && !loading && (
                        <div className="size-full z-20 absolute flex items-center justify-center grow">
                            <Suspense fallback={null}>
                                <InternalServerError />
                            </Suspense>
                        </div>
                    )}
                    {!loading && !error && (
                        filteredCompanies.length === 0 ? (
                            <>
                                <img alt="Empty box" width={80} height={80} src="/assets/empty-box.svg" />
                                <span className="text-[#333333] text-lg">{t("manageCompany.noCompaniesFound")}</span>
                                {searchFilter && (
                                    <button onClick={() => handleSearch("")} className="text-blue-600 hover:text-blue-800 text-sm">
                                        {t("manageCompany.clearSearch")}
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="overflow-x-auto rounded-xl border border-gray-100">
                                <table className="w-full text-sm">
                                    <thead>
                                    <tr className="bg-gray-50 text-left text-gray-500 text-xs uppercase tracking-wide">
                                        <th className="px-4 py-3 font-semibold">{t("manageCompany.columnCompany")}</th>
                                        <th className="px-4 py-3 font-semibold">{t("manageCompany.columnIndustry")}</th>
                                        <th className="px-4 py-3 font-semibold">{t("manageCompany.columnWebsite")}</th>
                                        <th className="px-4 py-3 font-semibold">{t("manageCompany.columnAddress")}</th>
                                        <th className="px-4 py-3 font-semibold">{t("manageCompany.columnCreatedOn")}</th>
                                        <th className="px-4 py-3 font-semibold">{t("manageCompany.columnActions")}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredCompanies.map((company) => (
                                        <CompanyRow
                                            key={company.id}
                                            company={company}
                                            onEdit={setEditingCompany}
                                            onDelete={setCompanyToDelete}
                                        />
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}
                </div>
                {/* Paginator */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between pr-16 rtl:pr-0 rtl:pl-16 gap-8 mt-4">
                        <span className="text-sm text-gray-500">
                            {t("surveys.list.paginationShowing", { from: currentPage * PAGE_SIZE + 1, to: Math.min((currentPage + 1) * PAGE_SIZE, totalItems), total: totalItems })}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setCurrentPage(p => p - 1)}
                                disabled={currentPage === 0}
                                className="w-9 h-9 rounded-2xl border-2 border-gray-800 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <svg className="rtl:-scale-x-100" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.83333 0.5L0.5 5.83333M0.5 5.83333L5.83333 11.1667M0.5 5.83333H11.1667" stroke="#333333" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i)
                                .filter(i => i === 0 || i === totalPages - 1 || Math.abs(i - currentPage) <= 1)
                                .reduce((acc, i, idx, arr) => {
                                    if (idx > 0 && i - arr[idx - 1] > 1) acc.push("...");
                                    acc.push(i);
                                    return acc;
                                }, [])
                                .map((item, idx) =>
                                    item === "..." ? (
                                        <span key={`ellipsis-${idx}`} className="w-9 h-9 flex items-center justify-center text-gray-400">…</span>
                                    ) : (
                                        <button
                                            key={item}
                                            onClick={() => setCurrentPage(item)}
                                            className={`w-9 h-9 rounded-2xl flex items-center justify-center text-sm font-medium transition-all ${
                                                item === currentPage
                                                    ? "bg-[#333333] text-white shadow-sm"
                                                    : "border-2 border-gray-800 text-gray-600 hover:bg-gray-50"
                                            }`}
                                        >
                                            {item + 1}
                                        </button>
                                    )
                                )}
                            <button
                                onClick={() => setCurrentPage(p => p + 1)}
                                disabled={currentPage === totalPages - 1}
                                className="w-9 h-9 rounded-2xl border-2 border-gray-800 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <svg className="rtl:-scale-x-100" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.83333 11.1667L11.1667 5.83333M11.1667 5.83333L5.83333 0.5M11.1667 5.83333H0.5" stroke="#333333" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </TaleDashbaordMainCard>
        </>
    );
};

export default Companies;